import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const HERO = "https://static.prod-images.emergentagent.com/jobs/12dc9125-d14c-48bb-a7a1-9e5310c2cf59/images/dfd5ecd4b8349f32f0ba1ffd65c2804812f9896b0029b12f3164a1ee0d3ec921.png";
const EDITORIAL = "https://images.pexels.com/photos/29090944/pexels-photo-29090944.jpeg";

// Editorial display names for the featured trio. Slugs map to the seeded products.
const FEATURED = [
  { editorial: "Ivory", slug: "signature-mini-ivory", colour: "ivory" },
  { editorial: "Noir", slug: "signature-mini-noir", colour: "noir" },
  { editorial: "Sable", slug: "signature-mini-sable", colour: "sable" },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const productBySlug = (slug) => products.find((p) => p.slug === slug);

  return (
    <main data-testid="home-page">
      {/* Hero - Editorial split */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:min-h-[88vh]">
          <div
            className="lg:col-span-7 relative overflow-hidden aspect-[4/5] lg:aspect-auto lg:h-auto"
            style={{ background: "var(--line-soft)" }}
          >
            <img
              src={HERO}
              alt="The Signature Mini"
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center" }}
            />
          </div>
          <div
            className="lg:col-span-5 flex items-center px-6 sm:px-12 lg:px-20 py-20 lg:py-0"
            style={{ background: "var(--bone)" }}
          >
            <div className="w-full">
              <p
                className="tracking-overline text-[11px] uppercase opacity-60 fade-up mt-4"
                data-testid="hero-eyebrow"
              >
                The Debut Edit
              </p>
              <h1
                className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-14 fade-up-delay-1"
                data-testid="hero-headline"
              >
                The <span className="italic">Signature</span> Mini.
              </h1>
              <div className="mt-14 max-w-[22rem] fade-up-delay-2" data-testid="hero-subhead">
                <p className="text-base leading-relaxed opacity-80">
                  A collection of three refined silhouettes shaped by clean tailoring and understated elegance. Created with intention and designed for versatility, each piece is made to feel timeless, effortless, and distinctly yours.
                </p>
              </div>
              <div className="mt-16 flex gap-4 fade-up-delay-3">
                <Link to="/shop" className="btn-primary" data-testid="hero-shop-button">
                  Shop the Collection
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand statement */}
      <section
        className="py-32 sm:py-44 px-6 text-center"
        style={{ background: "var(--bone)" }}
        data-testid="brand-statement"
      >
        <div className="max-w-2xl mx-auto">
          <p
            className="font-display italic leading-snug"
            style={{ fontSize: "clamp(1.75rem, 2.75vw, 2.5rem)" }}
          >
            We believe elegance is found in simplicity.
          </p>
          <p
            className="font-display mt-10 leading-snug opacity-80"
            style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}
          >
            Timeless pieces designed to be worn for years, not seasons.
          </p>
        </div>
      </section>

      {/* Marquee strip */}
      <section
        className="py-6 border-y overflow-hidden"
        style={{ borderColor: "var(--line)", background: "var(--bone-muted)" }}
      >
        <div className="marquee flex gap-16 whitespace-nowrap text-[11px] uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).flatMap((_, k) =>
            [
              "The Signature Mini",
              "Quiet Confidence",
              "Elegance, Redefined",
              "Beyond the Season",
              "Shipping across India",
            ].map((t, i) => (
              <span key={`${k}-${i}`}>
                {t} <span className="opacity-40 mx-4">◆</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* Featured trio - editorial cards */}
      <section
        className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-32 sm:py-44"
        data-testid="featured-section"
      >
        <div className="text-center max-w-xl mx-auto mb-24 sm:mb-32">
          <p
            className="text-[11px] tracking-[0.32em] uppercase"
            style={{ color: "rgba(26,26,25,0.55)" }}
          >
            The Edit
          </p>
          <h2
            className="font-display mt-8 leading-[1.05]"
            style={{ fontSize: "clamp(2.25rem, 3.5vw, 3.25rem)", fontWeight: 400 }}
          >
            Three considered <span className="italic">shades.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 lg:gap-x-16 gap-y-24">
          {FEATURED.map((entry, i) => {
            const product = productBySlug(entry.slug);
            const image = product?.images?.[0];
            return (
              <article
                key={entry.slug}
                className="flex flex-col fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
                data-testid={`featured-card-${entry.slug}`}
              >
                <Link to={`/shop/signature-mini?colour=${entry.colour}`} className="block group" aria-label={entry.editorial}>
                  <div
                    className="aspect-[4/5] overflow-hidden"
                    style={{ background: "#EFEBE4" }}
                  >
                    {image ? (
                      <img
                        src={image}
                        alt={entry.editorial}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        style={{ transition: "opacity 600ms ease, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.94")}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                      />
                    ) : (
                      <div className="w-full h-full" style={{ background: "#EFEBE4" }} />
                    )}
                  </div>
                </Link>

                <div className="mt-10 text-center px-2">
                  <h3
                    className="font-display tracking-[0.3em] uppercase"
                    style={{ fontSize: "clamp(1.5rem, 2vw, 2rem)", fontWeight: 400 }}
                    data-testid={`featured-name-${entry.slug}`}
                  >
                    {entry.editorial}
                  </h3>
                  <Link
                    to={`/shop/${entry.slug}`}
                    className="inline-block mt-8 text-[11px] uppercase tracking-[0.28em] underline underline-offset-[10px] decoration-1 hover:opacity-60 transition-opacity"
                    data-testid={`featured-cta-${entry.slug}`}
                  >
                    View Piece →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Editorial banner - dark section */}
      <section className="grid grid-cols-1 lg:grid-cols-12 min-h-[70vh]">
        <div
          className="lg:col-span-5 flex items-center px-6 sm:px-12 lg:px-20 py-20"
          style={{ background: "var(--ink-soft)", color: "var(--bone)" }}
        >
          <div>
            <p className="tracking-overline text-[11px] uppercase opacity-60">A Note From the Atelier</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Elegance is<br />
              <span className="italic">never accidental.</span>
            </h2>
            <p className="mt-8 leading-relaxed opacity-80 max-w-[31rem]">
              Tamoré was created with a simple belief: the most enduring pieces are often the simplest. Thoughtfully designed and carefully crafted, each piece is made to move effortlessly through seasons, bringing confidence, ease, and quiet elegance to everyday life.
            </p>
            <Link
              to="/about"
              className="atelier-link mt-10 text-[11px] uppercase tracking-[0.22em]"
              data-testid="editorial-read-more"
            >
              About Tamoré →
            </Link>
          </div>
        </div>
        <div
          className="lg:col-span-7 relative overflow-hidden min-h-[400px]"
          style={{ background: "var(--line-soft)" }}
        >
          <img src={EDITORIAL} alt="Editorial" className="absolute inset-0 object-cover w-full h-full" />
        </div>
      </section>

      {/* Values strip */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-12 pt-[4.8rem] pb-24 grid grid-cols-1 md:grid-cols-3 gap-12 border-t"
        style={{ borderColor: "var(--line)" }}
      >
        {[
          { t: "Quiet Confidence", d: "Designed to feel effortless, refined and enduring." },
          { t: "Beyond The Season", d: "Created to be worn for years, not just a season." },
          { t: "Considered Design", d: "Thoughtfully designed with balance, structure and ease." },
        ].map((c) => (
          <div key={c.t} className="flex flex-col gap-4">
            <h3 className="font-display text-2xl">{c.t}</h3>
            <p className="text-sm opacity-70 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </section>
    </main>
  );
}
